import { FastField, Field, Formik } from "formik";
import * as formsService from "../../services/formsService";
import { SimpleSingleSelect } from "../FormComponents/SimpleSingleSelect";
import { CurrencyInput } from "../FormComponents/CurrencyInput";
import { CTA, InputField } from "@coverme/forms";
import React, { useMemo, useState } from "react";
import CustomerEnabledFeature from "../CustomerEnabledFeature/CustomerEnabledFeature";
import { some } from "lodash-es";
import { formatCurrency } from "../../services/moneyFormatter";
import { extractCoverageCollected } from "../../services/coverageService";
import { ConfirmationModal } from "../../components/ConfirmationModal/ConfirmationModal";
import "./PayaForm.scss";

function extractCopayValues(coverageCollected) {
    if (coverageCollected?.copay !== undefined && coverageCollected?.copay !== null) {
        return {
            erInPlanNetwork: coverageCollected.copay.erInPlanNetwork,
            office: coverageCollected.copay.office,
            specialistInPlanNetwork: coverageCollected.copay.specialistInPlanNetwork,
        };
    } else {
        return null;
    }
}

export const PayaForm = ({
    encounterId,
    onSubmit,
    coverage,
    customer,
    copayLookup,
    deductibleOutOfPocket,
    history,
}) => {
    const features = customer.data.features || [];
    const [shouldShowContinueModal, setShouldShowContinueModal] = useState(false);

    const isFeatureEnabled = some(features, x => x === "Paya");

    const initialValues = {
        others: 0.0,
        othersDescription: "",
        copaySelected: [],
    };

    const copays = useMemo(() => {
        return extractCopayValues(extractCoverageCollected(coverage));
    }, [coverage]);

    function onModalConfirm() {
        history.push(`/interview/${encounterId}/details/dispatch`);
    }

    function onModalReject() {}

    return (
        <div id="header-section">
            <CustomerEnabledFeature requiredFeature="Paya">
                <h2 className="page-header">Payment Details</h2>
                <Formik
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    render={props => {
                        const { setFieldValue, setFieldTouched, errors, touched, values } = props;
                        const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                        const getErrors = formsService.getErrors.bind(this, errors);
                        let sum = isNaN(parseFloat(values.others)) ? 0 : parseFloat(values.others);
                        if (values.copaySelected.includes("ER")) {
                            values.transactionBreakdown = {
                                copayType: "ErInPlanNetwork",
                                copayAmount: copays?.erInPlanNetwork || 0,
                                others: values.others,
                                othersDescription: values.othersDescription,
                            };
                            sum += copays?.erInPlanNetwork || 0;
                        } else if (values.copaySelected.includes("Office")) {
                            values.transactionBreakdown = {
                                copayType: "Office",
                                copayAmount: copays?.office || 0,
                                others: values.others,
                                othersDescription: values.othersDescription,
                            };
                            sum += copays?.office || 0;
                        } else if (values.copaySelected.includes("Specialist")) {
                            values.transactionBreakdown = {
                                copayType: "SpecialistInPlanNetwork",
                                copayAmount: copays?.specialistInPlanNetwork || 0,
                                others: values.others,
                                othersDescription: values.othersDescription,
                            };
                            sum += copays?.specialistInPlanNetwork || 0;
                        }
                        values.transactionAmount = sum;

                        return (
                            <form id="payment" className="wizard-form">
                                <div className="form-section">
                                    {deductibleOutOfPocket !== undefined &&
                                        deductibleOutOfPocket !== null &&
                                        deductibleOutOfPocket.deductible > 0 && (
                                            <div className="form-control xl-input">
                                                <label
                                                    htmlFor={deductibleOutOfPocket.deductible}
                                                    className={`input-field__label`}
                                                >
                                                    {"Deductible: $" + deductibleOutOfPocket.deductible}
                                                </label>
                                            </div>
                                        )}
                                    {deductibleOutOfPocket !== undefined &&
                                        deductibleOutOfPocket !== null &&
                                        deductibleOutOfPocket.outOfPocket > 0 && (
                                            <div className="form-control xl-input">
                                                <label
                                                    htmlFor={deductibleOutOfPocket.outOfPocket}
                                                    className={`input-field__label`}
                                                >
                                                    {"Out of Pocket: $" + deductibleOutOfPocket.outOfPocket}
                                                </label>
                                            </div>
                                        )}
                                </div>
                                {copayLookup !== undefined && copayLookup !== null && copayLookup.length > 0 && (
                                    <div className="form-section">
                                        <div className="form-control xxl-input">
                                            <Field
                                                name="copaySelected"
                                                render={({ field }) => (
                                                    <SimpleSingleSelect
                                                        {...field}
                                                        id={field.name}
                                                        title={"Copay"}
                                                        setFieldTouched={setFieldTouched}
                                                        setFieldValue={setFieldValue}
                                                        errorMessage={getErrors(field)}
                                                        validState={getValidationState(field)}
                                                        required={false}
                                                        options={copayLookup}
                                                        matchFromStart={true}
                                                        changeEvent={() => {
                                                            sum = values.others;
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="form-section">
                                    <div className="form-control m-input">
                                        <Field name={`others`}>
                                            {({ field }) => (
                                                <CurrencyInput
                                                    {...field}
                                                    id={field.name}
                                                    value={field.value}
                                                    title={
                                                        copayLookup !== undefined &&
                                                        copayLookup !== null &&
                                                        copayLookup.length > 0
                                                            ? "Other"
                                                            : "Payment Amount"
                                                    }
                                                    placeholder={
                                                        copayLookup !== undefined &&
                                                        copayLookup !== null &&
                                                        copayLookup.length > 0
                                                            ? "Other"
                                                            : "Payment Amount"
                                                    }
                                                    required={false}
                                                    changeEvent={e => {
                                                        setFieldValue(field.name, e);
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div className="form-control m-input">
                                        <FastField
                                            name="othersDescription"
                                            render={({ field }) => (
                                                <InputField
                                                    id="othersDescription"
                                                    title={"Description"}
                                                    placeholder={"Description"}
                                                    errorMessage={getErrors(field)}
                                                    validState={getValidationState(field)}
                                                    required={false}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <p className="form-section h3 transaction-total">
                                    Transaction Total: {formatCurrency(sum)}
                                </p>

                                <div>
                                    <CTA
                                        url="/"
                                        size="large"
                                        ctaType="button"
                                        styleType="blue payaform_collect_Payment_buttons"
                                        id="submit-payment"
                                        disabled={sum <= 0 || !isFeatureEnabled}
                                        click={() => onSubmit(values)}
                                    >
                                        Collect Payment
                                    </CTA>
                                    <CTA
                                        url="/"
                                        size="large"
                                        ctaType="button"
                                        styleType="blue"
                                        id="submit-payment"
                                        click={() => {
                                            setShouldShowContinueModal(true);
                                        }}
                                    >
                                        Skip
                                    </CTA>
                                </div>
                                <ConfirmationModal
                                    showCloseButton={false}
                                    title={"Continue?"}
                                    body={<>Are you sure you want to skip the payment? This can't be undone</>}
                                    isShown={shouldShowContinueModal}
                                    onConfirm={() => {
                                        onModalConfirm();
                                    }}
                                    onReject={() => onModalReject()}
                                    onClose={() => {
                                        setShouldShowContinueModal(false);
                                    }}
                                />
                                {/* <p>
                                    <em>
                                        <q>
                                            By authorizing this transaction, customer agrees that merchant may convert
                                            this transaction into an Electronic Funds Transfer (EFT) transaction or
                                            paper draft, and to debit this account for the amount of the transaction.
                                            Additionally, in the event this draft or EFT is returned unpaid, a service
                                            fee, as allowable by law, will be charged to this account via EFT or draft.
                                            In the event you choose to revoke this authorization, please do so by
                                            contacting the merchant directly. Please note that processing times may not
                                            allow for revocation of this authorization
                                        </q>
                                    </em>
                                </p> */}
                            </form>
                        );
                    }}
                />
            </CustomerEnabledFeature>
        </div>
    );
};
